import { FRESHA_BOOKING_URL } from "../config";
import { Ban, Smile, Clock, Sparkles, Check } from "lucide-react";
import { LegalLayout, LegalSection } from "../components/LegalLayout";

const HERO_IMG =
  "https://images.unsplash.com/photo-1555820585-c5ae44394b79?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";
const HERO_VIDEO = "/futura-behandlung.mp4";
const DETAIL_IMG =
  "https://customer-assets-lqy194kg.emergentagent.net/job_216d3c35-aa83-4452-b36d-63c5224739d4/artifacts/o8v7h1zd_WhatsApp%20Image%202026-08-26%20at%2019.30.25.jpeg";

const highlights = [
  { icon: Ban, label: "Nadelfrei" },
  { icon: Smile, label: "Schmerzfrei" },
  { icon: Clock, label: "0% Ausfallzeit" },
  { icon: Sparkles, label: "Sofort sichtbar" },
];

const effects = [
  "Reduziert Bakterien, Pilze & Keime auf der Hautbarriere",
  "Beruhigt gerötete, entzündete und sensible Haut",
  "Aktiviert Zellerneuerung und Regeneration",
  "Stimuliert Kollagen & Elastin – strafft und glättet",
  "Optimiert die Haarstruktur und regt das Haarwachstum an",
  "Verfeinert das Hautbild und mindert Dehnungsstreifen",
];

const areas = ["Gesicht", "Hals", "Dekolleté", "Hände", "Haare", "Körper"];

const concerns = [
  "Anti-Aging",
  "Trockene Haut",
  "Hautrötungen",
  "Hyperpigmentierung",
  "Grosse Poren",
  "Unreine Haut",
  "Dehnungsstreifen",
  "Menopause",
];

const noNeedle = [
  "Gezielter Impuls auf Hautkontaktebene",
  "Keine thermische Wirkung während der Behandlung",
  "Kontrollierte, gleichmässige Energieabgabe",
  "Keine Rötung – und somit keine Ausfallzeit",
];

const serums = [
  { name: "Clinical Exosomen", text: "Biomimetische Exosomen, Neuro­wirkstoffe & Peptide für gezielte Hautregeneration, mehr Spannkraft und ein vitaleres Aussehen." },
  { name: "Clinical V-Lift Hyaluron", text: "Hyaluron ohne Spritze – konturiert das Gesicht, mildert Fältchen und strafft die Haut für eine frische Ausstrahlung." },
  { name: "Lipo Lift", text: "Spendet tiefenwirksam Feuchtigkeit, reduziert Schwellungen und stärkt die Hautbarriere – für glattere, straffere Haut." },
  { name: "Whitening Boost", text: "Hellt Pigmentflecken, Hyperpigmentierung und Altersflecken sichtbar auf – für einen ebenmässigen, strahlenden Teint." },
  { name: "Bakuchiol Hyaluron", text: "Sanfte Alternative für empfindliche Haut – fördert Kollagen, beruhigt und bekämpft Unreinheiten ohne Reizungen." },
  { name: "FuturaHair", text: "Fördert eine gesunde Kopfhaut, spendet Feuchtigkeit und regt durch gestärkte Haarfollikel das Haarwachstum an." },
];

export default function FuturaContour() {
  const hero = (
    <div
      className="relative w-full min-h-screen overflow-hidden bg-[#1A1A1A]"
      data-testid="futura-hero"
    >
      <video
        src={HERO_VIDEO}
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
        poster={HERO_IMG}
        data-testid="futura-hero-video"
        className="kenburns absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/10 to-black/85" />
      <div className="relative min-h-screen max-w-7xl mx-auto flex flex-col justify-end pb-16 md:pb-24 px-6 md:px-12 lg:px-24">
        <p className="text-sm tracking-[0.3em] uppercase text-[#C5A059] mb-4 fade-up">
          Unser Partner
        </p>
        <h1 className="font-playfair text-4xl sm:text-5xl lg:text-6xl text-white mb-4 fade-up fade-up-delay-1">
          Futura Contour®
        </h1>
        <p className="text-white/85 text-base md:text-lg max-w-xl fade-up fade-up-delay-2">
          Nadelfreie Hightech-Kosmetik nach medizinischem Standard – eine sanfte
          Alternative zu Fadenlifting und Unterspritzungen, ganz ohne Nadeln.
        </p>
      </div>
    </div>
  );

  return (
    <LegalLayout eyebrow="Unser Partner" title="Futura Contour®" hero={hero}>
      {/* Highlights */}
      <LegalSection>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="futura-highlights">
          {highlights.map((h) => (
            <div
              key={h.label}
              className="bg-white border border-[#E5E5E5] p-6 flex flex-col items-center text-center gap-3"
            >
              <div className="w-11 h-11 rounded-full bg-[#3d7d83]/12 flex items-center justify-center text-[#3d7d83]">
                <h.icon size={20} />
              </div>
              <span className="text-sm tracking-wide text-[#1A1A1A]">{h.label}</span>
            </div>
          ))}
        </div>
      </LegalSection>

      {/* Technology */}
      <LegalSection heading="Die Biphoretic-Technologie">
        <p>
          Futura Contour® setzt neue Massstäbe in der apparativen Kosmetik.
          Softwaregesteuert werden unterschiedlich phasierte Mikrostromimpulse
          und Niederfrequenzen kombiniert, um Wirkstoffe gezielt und
          transzellulär in die Haut einzubringen – schmerzfrei und ohne
          Ausfallzeiten.
        </p>
        <p>
          Die Steuerung erfolgt präzise über ein Handgelenk-System mit
          Fingeraufsätzen. So wird die natürliche Hautschutzbarriere punktuell
          geöffnet, damit hochwertige Wirkstoffe tief in die Zellen eindringen
          und ihre volle Wirkung entfalten können.
        </p>
      </LegalSection>

      {/* Effects */}
      <LegalSection heading="Was bewirkt Futura Contour® auf Ihrer Haut?">
        <div className="grid sm:grid-cols-2 gap-x-8 gap-y-5 mt-2" data-testid="futura-effects">
          {effects.map((e, i) => (
            <div key={i} className="flex gap-4">
              <span className="font-playfair text-xl text-[#C5A059] leading-none pt-0.5">
                {String(i + 1).padStart(2, "0")}
              </span>
              <p className="text-[#666666] leading-relaxed">{e}</p>
            </div>
          ))}
        </div>
      </LegalSection>

      {/* Treatment without needle */}
      <LegalSection heading="Behandlung ganz ohne Nadel">
        <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
          <div className="overflow-hidden border border-[#E5E5E5] bg-[#F9F8F6]">
            <img
              src={DETAIL_IMG}
              alt="Futura Contour® Gerät mit Wirkstoff-Produkten"
              className="w-full h-[300px] object-cover"
            />
          </div>
          <ul className="space-y-4">
            {noNeedle.map((b) => (
              <li key={b} className="flex gap-3 text-[#666666]">
                <Check size={20} className="text-[#C5A059] flex-shrink-0 mt-0.5" />
                <span className="leading-relaxed">{b}</span>
              </li>
            ))}
          </ul>
        </div>
      </LegalSection>

      {/* Areas + concerns */}
      <LegalSection heading="Anwendungsbereiche">
        <p className="mb-6">
          Mit bis zu 6 ml Hyaluron behandelt Futura Contour® gezielt und sanft –
          mit sofort sichtbaren Ergebnissen an folgenden Bereichen:
        </p>
        <div className="flex flex-wrap gap-3 mb-10" data-testid="futura-areas">
          {areas.map((a) => (
            <span
              key={a}
              className="px-5 py-2 rounded-full bg-[#1A1A1A] text-white text-sm tracking-wide"
            >
              {a}
            </span>
          ))}
        </div>
        <p className="mb-4 text-[#1A1A1A] font-medium">Ideal bei:</p>
        <div className="flex flex-wrap gap-3">
          {concerns.map((c) => (
            <span
              key={c}
              className="px-4 py-2 rounded-full border border-[#C5A059] text-[#1A1A1A] text-sm"
            >
              {c}
            </span>
          ))}
        </div>
      </LegalSection>

      {/* Premium ingredients */}
      <LegalSection heading="Premium-Wirkstoffe – Made in Germany">
        <p className="mb-8">
          Mehrfach komplexierte Wirkstoffsysteme aus Hyaluron-Layering,
          Peptidkomplexen und Neurowirkstoffen wirken an der Oberfläche, in der
          Dermis sowie auf zellulärer Ebene – vollständig frei von
          Phenoxyethanol, Parabenen und aggressiven Konservierungsstoffen.
        </p>
        <div className="grid sm:grid-cols-2 gap-6" data-testid="futura-serums">
          {serums.map((s) => (
            <div key={s.name} className="bg-white border border-[#E5E5E5] border-t-2 border-t-[#3d7d83] p-6">
              <h3 className="font-playfair text-lg text-[#1A1A1A] mb-2">{s.name}</h3>
              <p className="text-sm text-[#666666] leading-relaxed">{s.text}</p>
            </div>
          ))}
        </div>
      </LegalSection>

      {/* CTA */}
      <LegalSection>
        <div className="bg-gradient-to-br from-[#1A1A1A] via-[#173c40] to-[#1A1A1A] text-white p-10 md:p-14 text-center">
          <p className="font-cormorant italic text-2xl md:text-3xl mb-4">
            Bereit für Ihr schönstes Selbst?
          </p>
          <p className="text-white/70 max-w-xl mx-auto mb-8">
            Erleben Sie die Vorteile von Futura Contour® in unserem Studio in
            Klosters. Vereinbaren Sie Ihr persönliches Beratungsgespräch.
          </p>
          <a
            href={FRESHA_BOOKING_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="futura-cta"
            className="inline-block bg-[#C5A059] text-[#1A1A1A] px-8 py-4 rounded-full text-sm tracking-wider uppercase font-medium hover:bg-[#d4b06a] transition-colors duration-300"
          >
            Beratungsgespräch vereinbaren
          </a>
        </div>
      </LegalSection>
    </LegalLayout>
  );
}
